/**
 * useScrollSnap - Detect which item is centered in a horizontal scroll container
 *
 * This hook monitors scroll position and determines which item is currently
 * centered in the viewport. It's perfect for implementing horizontal carousels
 * with snap-to-center behavior.
 *
 * @example
 * ```tsx
 * const containerRef = useRef<HTMLDivElement>(null)
 * const { centeredIndex, scrollToIndex } = useScrollSnap({
 *   containerRef,
 *   itemCount: items.length,
 *   onCenterChange: (index) => console.log('Centered item:', index)
 * })
 * ```
 */

import { useEffect, useState, useCallback, RefObject } from 'react'

interface UseScrollSnapOptions {
  /** Ref to the scroll container */
  containerRef: RefObject<HTMLDivElement>
  /** Total number of items in the container */
  itemCount: number
  /** Callback fired when the centered item changes */
  onCenterChange?: (index: number) => void
}

interface UseScrollSnapReturn {
  /** Index of the currently centered item */
  centeredIndex: number
  /** Programmatically scroll to a specific index */
  scrollToIndex: (index: number) => void
}

/**
 * Calculate which item is currently centered in the scroll container
 */
function calculateCenteredIndex(
  container: HTMLDivElement,
  itemCount: number
): number {
  if (itemCount === 0) return 0

  const containerRect = container.getBoundingClientRect()
  const containerCenter = containerRect.left + containerRect.width / 2

  const items = Array.from(container.children) as HTMLElement[]

  if (items.length === 0) return 0

  // Find the item whose center is closest to the container center
  let closestIndex = 0
  let closestDistance = Infinity

  items.forEach((item, index) => {
    const itemRect = item.getBoundingClientRect()
    const itemCenter = itemRect.left + itemRect.width / 2
    const distance = Math.abs(containerCenter - itemCenter)

    if (distance < closestDistance) {
      closestDistance = distance
      closestIndex = index
    }
  })

  return closestIndex
}

/**
 * Custom hook to detect which item is centered during horizontal scrolling
 */
export function useScrollSnap({
  containerRef,
  itemCount,
  onCenterChange,
}: UseScrollSnapOptions): UseScrollSnapReturn {
  const [centeredIndex, setCenteredIndex] = useState(0)

  /**
   * Programmatically scroll to a specific item
   */
  const scrollToIndex = useCallback(
    (index: number) => {
      const container = containerRef.current
      if (!container) return

      const items = Array.from(container.children) as HTMLElement[]
      const targetItem = items[index]

      if (!targetItem) return

      // Calculate the scroll position to center this item
      const containerRect = container.getBoundingClientRect()
      const itemRect = targetItem.getBoundingClientRect()

      const containerCenter = containerRect.width / 2
      const itemCenter = itemRect.left - containerRect.left + itemRect.width / 2
      const scrollOffset = itemCenter - containerCenter

      container.scrollBy({
        left: scrollOffset,
        behavior: 'smooth',
      })
    },
    [containerRef]
  )

  /**
   * Handle scroll events with debouncing
   */
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    let rafId: number | null = null
    let lastIndex = centeredIndex

    const handleScroll = () => {
      // Cancel any pending animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }

      // Use requestAnimationFrame for smooth updates
      rafId = requestAnimationFrame(() => {
        const newIndex = calculateCenteredIndex(container, itemCount)

        if (newIndex !== lastIndex) {
          lastIndex = newIndex
          setCenteredIndex(newIndex)
          onCenterChange?.(newIndex)
        }

        rafId = null
      })
    }

    // Initial calculation
    handleScroll()

    // Listen to scroll events
    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      if (rafId !== null) {
        cancelAnimationFrame(rafId)
      }
    }
  }, [containerRef, itemCount, onCenterChange])

  return {
    centeredIndex,
    scrollToIndex,
  }
}
